/**
 * Confidence indicator component displaying confidence score with optional bar and explanation popover.
 */

import classNames from 'classnames'
import React from 'react'
import { Popover, PopoverInteractionKind, Position } from '@blueprintjs/core'
import { getConfidenceConfig } from './ai-block-activity-helpers'

interface IConfidenceIndicatorProps {
  confidence?: number
  explanation?: string
  showBar?: boolean
}

export const ConfidenceIndicator: React.FC<IConfidenceIndicatorProps> = ({
  confidence,
  explanation,
  showBar = false,
}) => {
  if (confidence == null) {
    return <span className='c-aiBlockResults-emDash'>—</span>
  }

  const { color, shortLabel } = getConfidenceConfig(confidence)

  const confidenceCell = (
    <div className='c-aiBlockResults-confidenceInner'>
      {showBar && (
        <div className='c-aiBlockResults-confidenceBar'>
          <div
            className={classNames(
              'c-aiBlockResults-confidenceBar-fill',
              `c-aiBlockResults-confidenceBar-fill--${color}`,
            )}
            style={{ width: `${confidence}%` }}
          />
        </div>
      )}
      <span
        className={classNames(
          'c-aiBlockResults-confidenceScore',
          `c-aiBlockResults-confidenceScore--${color}`,
        )}
        title={`${confidence}%`}
      >
        {shortLabel}
      </span>
    </div>
  )

  const hasPopoverContent = Boolean(explanation)
  if (!hasPopoverContent) {
    return <div className='c-aiBlockResults-confidenceWrapper'>{confidenceCell}</div>
  }

  return (
    <Popover
      interactionKind={PopoverInteractionKind.HOVER}
      position={Position.TOP}
      content={
        <div className='c-aiBlockResults-explanationPopover'>
          {confidence != null && (
            <div className='c-aiBlockResults-explanationPopover-title'>Score: {confidence}%</div>
          )}
          {explanation && (
            <div className='c-aiBlockResults-explanationPopover-body'>{explanation}</div>
          )}
        </div>
      }
    >
      <div className='c-aiBlockResults-confidenceWrapper c-aiBlockResults-confidenceWrapper--hoverable'>
        {confidenceCell}
      </div>
    </Popover>
  )
}
